import type { ClientIndicators, DashboardMode, SmartStatus } from '@/types'
import { getStatusLevel } from '@/utils/indicators'
import { SMART_STATUS_CLASSES, SMART_STATUS_LABELS } from '@/utils/status'

interface ClientTableProps {
  indicators: ClientIndicators[]
  mode: DashboardMode
  onClientClick: (indicators: ClientIndicators) => void
}

export function ClientTable({ indicators, mode, onClientClick }: ClientTableProps) {
  const doneHeader = mode === 'production' ? 'Prontos' : 'Agendados'

  if (indicators.length === 0) {
    return (
      <div className="card p-8 text-center text-brand-taupe">
        Nenhum cliente selecionado.
      </div>
    )
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-brand-beige">
        <h2 className="text-sm font-semibold text-brand-black uppercase tracking-wider">
          Por cliente
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brand-beige-light border-b border-brand-beige">
              <th className="text-left px-6 py-3 text-xs font-semibold text-brand-taupe uppercase tracking-wider">
                Cliente
              </th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-brand-taupe uppercase tracking-wider">
                Total
              </th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-brand-taupe uppercase tracking-wider">
                {doneHeader}
              </th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-brand-taupe uppercase tracking-wider">
                Pendentes
              </th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-brand-taupe uppercase tracking-wider">
                %
              </th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-brand-taupe uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-beige">
            {indicators.map((ind) => (
              <ClientRow
                key={ind.client.boardId}
                indicators={ind}
                onClick={() => onClientClick(ind)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface ClientRowProps {
  indicators: ClientIndicators
  onClick: () => void
}

function ClientRow({ indicators: ind, onClick }: ClientRowProps) {
  const { client, totalCards, doneCards, pendingCards, percentage, smartStatus, error } = ind

  if (error) {
    return (
      <tr className="hover:bg-brand-beige-light/50 transition-colors">
        <td className="px-6 py-4 font-medium text-brand-black">{client.name}</td>
        <td colSpan={5} className="px-4 py-4 text-center text-red-500 text-xs">
          {error}
        </td>
      </tr>
    )
  }

  const statusLevel = getStatusLevel(percentage)

  return (
    <tr
      className="hover:bg-brand-beige-light/50 transition-colors cursor-pointer group"
      onClick={onClick}
    >
      {/* Client name */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <span className="font-medium text-brand-black group-hover:text-brand-taupe transition-colors">
            {client.name}
          </span>
          {totalCards === 0 && (
            <span className="badge bg-brand-beige text-brand-taupe">sem cards</span>
          )}
        </div>
      </td>

      {/* Total */}
      <td className="px-4 py-4 text-center text-brand-black font-medium">
        {totalCards}
      </td>

      {/* Done */}
      <td className="px-4 py-4 text-center text-green-600 font-medium">
        {doneCards}
      </td>

      {/* Pending */}
      <td className="px-4 py-4 text-center text-brand-taupe">
        {pendingCards}
      </td>

      {/* Percentage + bar */}
      <td className="px-4 py-4 text-center">
        <div className="flex flex-col items-center gap-1">
          <span
            className={`font-semibold text-sm ${
              statusLevel === 'green'
                ? 'text-green-600'
                : statusLevel === 'yellow'
                ? 'text-yellow-600'
                : 'text-red-500'
            }`}
          >
            {percentage}%
          </span>
          <div className="w-16 h-1 rounded-full bg-brand-beige overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                statusLevel === 'green'
                  ? 'bg-green-500'
                  : statusLevel === 'yellow'
                  ? 'bg-yellow-400'
                  : 'bg-red-400'
              }`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
      </td>

      {/* Status badge */}
      <td className="px-4 py-4 text-center">
        <StatusBadge status={smartStatus} />
      </td>
    </tr>
  )
}

function StatusBadge({ status }: { status: SmartStatus }) {
  return (
    <span className={`badge ${SMART_STATUS_CLASSES[status]} text-xs`}>
      ● {SMART_STATUS_LABELS[status]}
    </span>
  )
}
